import React from 'react';
import { DynamicColorIOS, Platform, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeTabs } from 'expo-router/unstable-native-tabs';
import { Tabs } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FootballFieldTabBarBackground from '@/components/navigation/FootballFieldTabBarBackground';
import LeagueLayoutStatus from '@/components/navigation/LeagueLayoutStatus';
import {
  getVisibleLeagueTabs,
  isLeagueTabVisible,
  LEAGUE_TAB_NAMES,
  usesNativeLeagueTabs,
  type LeagueTabName,
  type LeagueTabVisibilityInput,
} from '@/components/navigation/leagueTabVisibility';
import AppHeader from '@/components/AppHeader';
import { useLeagueContext } from '@/contexts/LeagueContext';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';
import { getLeagueHref } from '@/lib/routes';
import { clientLogger } from '@/lib/datadog';
import { useTRPC } from '@/lib/trpc';

function TabIcon({
  color,
  iconName,
  size,
}: {
  color: React.ComponentProps<typeof Ionicons>['color'];
  iconName: React.ComponentProps<typeof Ionicons>['name'];
  size: number;
}) {
  return <Ionicons name={iconName} size={size} color={color} />;
}

type LeagueTabIcon = {
  ionicon: React.ComponentProps<typeof Ionicons>['name'];
  icon: React.ComponentProps<typeof NativeTabs.Trigger.Icon>;
};

const LEAGUE_TAB_ICONS: Record<LeagueTabName, LeagueTabIcon> = {
  picks: {
    ionicon: 'checkbox-outline',
    icon: {
      sf: { default: 'checkmark.square', selected: 'checkmark.square.fill' },
      md: { default: 'check_box_outline_blank', selected: 'check_box' },
    },
  },
  draft: {
    ionicon: 'create-outline',
    icon: { sf: 'square.and.pencil', md: 'edit' },
  },
  scores: {
    ionicon: 'trophy-outline',
    icon: { sf: { default: 'trophy', selected: 'trophy.fill' }, md: 'emoji_events' },
  },
  leaderboard: {
    ionicon: 'trophy-outline',
    icon: { sf: { default: 'trophy', selected: 'trophy.fill' }, md: 'emoji_events' },
  },
  bracket: {
    ionicon: 'git-branch-outline',
    icon: { sf: 'point.3.connected.trianglepath.dotted', md: 'account_tree' },
  },
  standings: {
    ionicon: 'list-outline',
    icon: { sf: 'list.bullet', md: 'format_list_bulleted' },
  },
  rosters: {
    ionicon: 'people-outline',
    icon: {
      sf: { default: 'person.2', selected: 'person.2.fill' },
      md: { default: 'group', selected: 'groups' },
    },
  },
  chat: {
    ionicon: 'chatbubble-ellipses-outline',
    icon: {
      sf: { default: 'bubble.left.and.bubble.right', selected: 'bubble.left.and.bubble.right.fill' },
      md: { default: 'chat_bubble_outline', selected: 'chat_bubble' },
    },
  },
  stats: {
    ionicon: 'bar-chart-outline',
    icon: { sf: { default: 'chart.bar', selected: 'chart.bar.fill' }, md: 'bar_chart' },
  },
  settings: {
    ionicon: 'settings-outline',
    icon: { sf: { default: 'gearshape', selected: 'gearshape.fill' }, md: 'settings' },
  },
};

const LEAGUE_TAB_TITLES: Record<LeagueTabName, string> = {
  picks: 'Picks',
  draft: 'Draft',
  scores: 'Scores',
  leaderboard: 'Achievements',
  bracket: 'Bracket',
  standings: 'Standings',
  rosters: 'Rosters',
  chat: 'Chat',
  stats: 'Stats',
  settings: 'Settings',
};

export default function LeagueTabsLayout() {
  const leagueData = useLeagueContext();
  const theme = useTheme<Theme>();
  const insets = useSafeAreaInsets();
  const { width: screenWidth } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && screenWidth >= 768;
  const trpc = useTRPC();
  const isChatEnabled = leagueData.league?.chatEnabled === true;
  const permissionsReady = !leagueData.permissionsLoading || Boolean(leagueData.permissions);
  const { data: unreadSummary } = useQuery(
    trpc.chat.unreadSummary.queryOptions(undefined, {
      enabled: isChatEnabled,
      staleTime: 15_000,
      refetchInterval: 15_000,
    }),
  );

  // Native tabs remount when the visible set changes. Wait until permissions
  // are known so the settings tab does not appear a moment later and reset
  // the bar. Web tabs can update hrefs in place.
  if (Platform.OS !== 'web' && !permissionsReady) {
    return (
      <>
        <AppHeader title="Loading..." allowBack={false} />
        <LeagueLayoutStatus state="loading" />
      </>
    );
  }

  const visibility: LeagueTabVisibilityInput = {
    isMarchMadness: leagueData.isMarchMadness,
    showsTournamentBracket: leagueData.isMarchMadness || leagueData.isWorldCupTournament,
    isChatEnabled,
    showSettingsTab: Boolean(leagueData.permissions?.isOwner || leagueData.permissions?.isManager),
  };
  const visibleTabs = getVisibleLeagueTabs(visibility);
  const tabBarBottomPadding = Math.max(insets.bottom, 12);
  const tabRouteOptions = { seasonId: leagueData.resolvedSeasonId };
  const chatUnreadCount =
    unreadSummary?.entries.find((entry) => entry.leagueId === leagueData.id)?.unreadCount ?? 0;
  const useNativeTabs = usesNativeLeagueTabs(Platform.OS, visibleTabs.length);

  if (__DEV__) {
    clientLogger.debug('[LeagueTabsLayout] Rendering tabs', {
      leagueId: leagueData.id,
      useNativeTabs,
      visibleTabs,
    });
  }

  if (useNativeTabs) {
    const tintColor =
      Platform.OS === 'ios'
        ? DynamicColorIOS({
            dark: theme.colors.white,
            light: theme.colors.press,
          })
        : theme.colors.press;

    return (
      <NativeTabs
        disableTransparentOnScrollEdge
        minimizeBehavior="automatic"
        tintColor={tintColor}
        iconColor={{ default: theme.colors.pressMuted, selected: tintColor }}
        labelStyle={{
          default: { color: theme.colors.pressMuted },
          selected: { color: tintColor },
        }}
        indicatorColor={theme.colors.field}
        badgeBackgroundColor={theme.colors.maroon}
      >
        {LEAGUE_TAB_NAMES.map((name) => {
          const hidden = !isLeagueTabVisible(name, visibility);
          if (hidden) {
            return <NativeTabs.Trigger key={name} name={name} hidden />;
          }

          const badge = name === 'chat' && chatUnreadCount > 0 ? String(chatUnreadCount) : undefined;
          return (
            <NativeTabs.Trigger
              key={name}
              name={name}
              contentStyle={{ backgroundColor: theme.colors.background }}
              // iOS keeps the last badge when the prop is omitted. An empty
              // string is what react-native-screens treats as no badge.
              unstable_nativeProps={
                Platform.OS === 'ios' && name === 'chat'
                  ? { badgeValue: badge ?? '' }
                  : undefined
              }
            >
              <NativeTabs.Trigger.Label>{LEAGUE_TAB_TITLES[name]}</NativeTabs.Trigger.Label>
              <NativeTabs.Trigger.Icon {...LEAGUE_TAB_ICONS[name].icon} />
              {badge ? <NativeTabs.Trigger.Badge>{badge}</NativeTabs.Trigger.Badge> : null}
            </NativeTabs.Trigger>
          );
        })}
      </NativeTabs>
    );
  }

  return (
    <Tabs
      screenOptions={{
        headerShown: false,
        tabBarPosition: isDesktopWeb ? 'top' : 'bottom',
        tabBarActiveTintColor: isDesktopWeb ? theme.colors.white : theme.colors.press,
        tabBarInactiveTintColor: isDesktopWeb ? theme.colors.primary100 : theme.colors.pressMuted,
        tabBarActiveBackgroundColor: isDesktopWeb ? 'rgba(11, 63, 43, 0.44)' : theme.colors.successBackground,
        tabBarBackground: isDesktopWeb ? () => <FootballFieldTabBarBackground /> : undefined,
        tabBarLabelStyle: {
          fontFamily: theme.typography.fontFamily.mono,
          fontSize: isDesktopWeb ? 12 : 11,
          fontWeight: '700',
          textTransform: 'uppercase',
        },
        tabBarIconStyle: isDesktopWeb ? { display: 'none' } : undefined,
        tabBarStyle: isDesktopWeb
          ? {
              alignSelf: 'stretch',
              backgroundColor: theme.colors.field,
              borderBottomWidth: 0,
              borderRadius: 0,
              borderTopWidth: 0,
              height: 48,
              paddingBottom: 0,
              paddingHorizontal: 16,
              paddingTop: 0,
              width: '100%',
            }
          : {
              alignSelf: 'stretch',
              backgroundColor: theme.colors.background,
              borderColor: theme.colors.press,
              borderRadius: 0,
              borderTopColor: theme.colors.border,
              borderTopWidth: 5,
              height: 62 + tabBarBottomPadding,
              paddingBottom: tabBarBottomPadding,
              paddingHorizontal: 0,
              paddingTop: 4,
              width: '100%',
            },
        tabBarItemStyle: isDesktopWeb
          ? {
              borderRadius: 0,
              flex: 0,
              marginHorizontal: 4,
              minWidth: 104,
            }
          : {
              borderRadius: 0,
            },
      }}
    >
      {LEAGUE_TAB_NAMES.map((name) => {
        const visible = isLeagueTabVisible(name, visibility);
        return (
          <Tabs.Screen
            key={name}
            name={name}
            options={{
              href: visible ? getLeagueHref(leagueData.id, name, tabRouteOptions) : null,
              title: LEAGUE_TAB_TITLES[name],
              tabBarBadge: name === 'chat' && chatUnreadCount > 0 ? String(chatUnreadCount) : undefined,
              tabBarIcon: ({ color, size }) => (
                <TabIcon color={color} size={size} iconName={LEAGUE_TAB_ICONS[name].ionicon} />
              ),
            }}
          />
        );
      })}
    </Tabs>
  );
}
